/**
 * Workflows tab - manage case workflow templates and steps (Settings → Case Module → Workflows)
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon } from '@mui/icons-material';
import apiService from '../services/api';

export interface WorkflowTemplate {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  step_count?: number;
}

export interface WorkflowTemplateStep {
  id?: string;
  name: string;
  description?: string;
  sort_order: number;
}

interface WorkflowsTabProps {
  onError?: (msg: string) => void;
}

const WorkflowsTab: React.FC<WorkflowsTabProps> = ({ onError }) => {
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WorkflowTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', is_active: true });
  const [steps, setSteps] = useState<WorkflowTemplateStep[]>([]);
  const [saving, setSaving] = useState(false);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await apiService.listCaseWorkflowTemplates(false);
      setTemplates(list || []);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Failed to load workflow templates';
      setError(msg);
      if (onError) onError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleOpenAdd = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: '', description: '', is_active: true });
    setSteps([]);
    setDialogOpen(true);
  };

  const handleOpenEdit = async (t: WorkflowTemplate) => {
    setEditingTemplate(t);
    setTemplateForm({
      name: t.name,
      description: t.description || '',
      is_active: t.is_active,
    });
    try {
      const stepList = await apiService.getCaseWorkflowTemplateSteps(t.id);
      setSteps(
        (stepList || []).map((s: any) => ({
          id: s.id,
          name: s.name,
          description: s.description || '',
          sort_order: s.sort_order ?? 0,
        }))
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load steps');
      setSteps([]);
    }
    setDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    const name = templateForm.name.trim();
    if (!name) {
      setError('Name is required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editingTemplate) {
        await apiService.updateCaseWorkflowTemplate(editingTemplate.id, {
          name,
          description: templateForm.description.trim() || undefined,
          is_active: templateForm.is_active,
        });
        const stepsToSend = steps.map((s, i) => ({
          name: s.name.trim(),
          description: s.description?.trim() || undefined,
          sort_order: i,
        }));
        await apiService.replaceCaseWorkflowTemplateSteps(editingTemplate.id, stepsToSend);
      } else {
        const created = await apiService.createCaseWorkflowTemplate({
          name,
          description: templateForm.description.trim() || undefined,
          is_active: templateForm.is_active,
        });
        if (steps.length > 0) {
          const stepsToSend = steps.map((s, i) => ({
            name: s.name.trim(),
            description: s.description?.trim() || undefined,
            sort_order: i,
          }));
          await apiService.replaceCaseWorkflowTemplateSteps(created.id, stepsToSend);
        }
      }
      await fetchTemplates();
      setDialogOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (t: WorkflowTemplate) => {
    if (!window.confirm(`Delete workflow "${t.name}"? This cannot be undone.`)) return;
    try {
      setError(null);
      await apiService.deleteCaseWorkflowTemplate(t.id);
      await fetchTemplates();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const addStep = () => {
    setSteps((prev) => [...prev, { name: '', description: '', sort_order: prev.length }]);
  };

  const updateStep = (index: number, field: 'name' | 'description', value: string) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const removeStep = (index: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, sort_order: i })));
  };

  const moveStep = (index: number, delta: number) => {
    const next = index + delta;
    if (next < 0 || next >= steps.length) return;
    setSteps((prev) => {
      const copy = [...prev];
      [copy[index], copy[next]] = [copy[next], copy[index]];
      return copy.map((s, i) => ({ ...s, sort_order: i }));
    });
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define workflow templates (e.g. NBN FTTN to FTTP upgrade) with ordered steps. Apply a workflow to a case from the case detail page to track progress (open / in progress / complete).
      </Typography>
      <Paper>
        <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1.5}>
          <Typography variant="subtitle1">Workflow templates</Typography>
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={handleOpenAdd}>
            Add workflow
          </Button>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Steps</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right" width={120}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }} color="text.secondary">
                      No workflow templates yet. Add one to define a repeatable process (e.g. NBN upgrade steps).
                    </TableCell>
                  </TableRow>
                ) : (
                  templates.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>{t.name}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>{t.description || '—'}</TableCell>
                      <TableCell>{t.step_count ?? 0}</TableCell>
                      <TableCell>{t.is_active ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenEdit(t)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteTemplate(t)} title="Delete" color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTemplate ? 'Edit workflow template' : 'Add workflow template'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={templateForm.name}
            onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
            fullWidth
            margin="normal"
            placeholder="e.g. NBN FTTN to FTTP upgrade"
          />
          <TextField
            label="Description"
            value={templateForm.description}
            onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />
          <FormControlLabel
            control={
              <Switch
                checked={templateForm.is_active}
                onChange={(e) => setTemplateForm({ ...templateForm, is_active: e.target.checked })}
              />
            }
            label="Active (available when applying to cases)"
            sx={{ mt: 1 }}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Steps (order matters)
          </Typography>
          <List dense disablePadding>
            {steps.map((step, index) => (
              <ListItem key={index} divider sx={{ alignItems: 'flex-start', gap: 0.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Step name"
                    value={step.name}
                    onChange={(e) => updateStep(index, 'name', e.target.value)}
                    sx={{ mb: 0.5 }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Description (optional)"
                    value={step.description || ''}
                    onChange={(e) => updateStep(index, 'description', e.target.value)}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <IconButton size="small" onClick={() => moveStep(index, -1)} disabled={index === 0} aria-label="Move up">
                    <ArrowUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => moveStep(index, 1)} disabled={index === steps.length - 1} aria-label="Move down">
                    <ArrowDownIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => removeStep(index)} color="error" aria-label="Remove">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
          <Button startIcon={<AddIcon />} size="small" onClick={addStep} sx={{ mt: 1 }}>
            Add step
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveTemplate} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editingTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkflowsTab;
